import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCategoryPermissionStatus } from '../../../api/adminAPI';
import { newGetCategory } from '../../../api/categoryAPI';
import { getBoardHeaderInfo } from '../config/boardHeaderStructure';
import type { Board } from './useBoards';

export type PermissionStatus = 'ALL' | 'PARTIAL' | 'NONE';

/** 카테고리 별 권한을 사용하는 게시판(정규화된 형태) */
export interface PermissionBoard {
    boardId: number;
    name: string;
    label: string;
    order: number;
    rootCategoryId: number;
}

export interface BoardCategory {
    id: number;
    name: string;
}

/** 카테고리 별 권한이 활성화된 게시판만 추려 정규화 */
const buildPermBoards = (source: Board[]): PermissionBoard[] =>
    source
        .filter(
            (b) =>
                b.isPermissionEnabled &&
                b.isCategoryPermissionEnabled &&
                b.rootCategories?.[0]?.id != null,
        )
        .map((b) => {
            const info = getBoardHeaderInfo(b.name);
            return {
                boardId: b.board_id,
                name: b.name,
                label: info.label,
                order: info.order,
                rootCategoryId: b.rootCategories[0].id as number,
            };
        })
        .sort((a, b) => a.order - b.order);

/**
 * "카테고리 별 권한"이 켜진 게시판만 추려서, 각 유저가 게시판별로 어떤 권한 상태
 * (ALL/PARTIAL/NONE)를 가졌는지 한 번에 조회하는 훅.
 *
 * - statusByUser[userId][boardId] = 'ALL' | 'PARTIAL' | 'NONE'
 * - categoriesByBoard[boardId]    = 해당 게시판 root 하위의 최상위 카테고리 목록
 *
 * 게시판 수 만큼(보통 한 자릿수) 상태 API를 호출하며, 각 호출은 전체 유저의 상태를
 * 한 번에 반환하므로 유저 수와 무관하게 호출 횟수가 적다.
 */
export const useUserBoardPermissions = (boards: Board[]) => {
    const [permBoards, setPermBoards] = useState<PermissionBoard[]>([]);
    const [statusByUser, setStatusByUser] = useState<
        Record<number, Record<number, PermissionStatus>>
    >({});
    const [categoriesByBoard, setCategoriesByBoard] = useState<
        Record<number, BoardCategory[]>
    >({});
    const [loading, setLoading] = useState(false);

    // boards 는 로딩 중 매 렌더마다 새 참조가 될 수 있으므로, 실제로 의미 있는
    // 변화(게시판/루트카테고리 구성)만 감지하도록 원시값 시그니처로 게이팅한다.
    const rawPermBoards = useMemo(() => buildPermBoards(boards), [boards]);
    const signature = useMemo(
        () => rawPermBoards.map((b) => `${b.boardId}:${b.rootCategoryId}`).join(','),
        [rawPermBoards],
    );

    const fetchAll = useCallback(async (targetBoards: PermissionBoard[]) => {
        if (targetBoards.length === 0) {
            setStatusByUser({});
            setCategoriesByBoard({});
            return;
        }

        setLoading(true);
        try {
            const statusMap: Record<number, Record<number, PermissionStatus>> = {};
            const categoryMap: Record<number, BoardCategory[]> = {};

            await Promise.all(
                targetBoards.map(async (board) => {
                    const [statusRes, categoryRes] = await Promise.all([
                        getCategoryPermissionStatus(board.rootCategoryId),
                        newGetCategory(board.rootCategoryId),
                    ]);

                    categoryMap[board.boardId] = (categoryRes || []).map((c: any) => ({
                        id: c.id ?? c.categoryId ?? c.category_id,
                        name: c.name,
                    }));

                    const users = statusRes?.data || [];
                    users.forEach((u: any) => {
                        if (!statusMap[u.userId]) statusMap[u.userId] = {};
                        statusMap[u.userId][board.boardId] =
                            (u.permissionStatus as PermissionStatus) || 'NONE';
                    });
                }),
            );

            setStatusByUser(statusMap);
            setCategoriesByBoard(categoryMap);
        } catch (error) {
            console.error('유저 게시판 권한 상태 조회 실패:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setPermBoards(rawPermBoards);
        fetchAll(rawPermBoards);
        // signature 가 바뀔 때만(게시판 구성 변경) 재조회
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature]);

    const refetch = useCallback(() => fetchAll(rawPermBoards), [fetchAll, rawPermBoards]);

    return { permBoards, statusByUser, categoriesByBoard, loading, refetch };
};
